import {
  concat,
  fromArray,
  fromPromise,
  fromValue,
  make,
  map,
  mergeMap,
  pipe,
  tap,
} from 'wonka';
import { Command, CommandArray } from './@types/commands';
import { sanitiseCommand } from './command';
import { addOptions, getEnvironment } from './environment';
import { log } from './logger';

export const getFirstCommand = (globalScope = window) => {
  try {
    const mobkoiNamespace = globalScope.MOBKOI;

    if (!Array.isArray(mobkoiNamespace)) {
      throw new Error('MOBKOI namespace is not an array');
    }

    const [command, script, tagId, pageOptions, options] =
      mobkoiNamespace.find(
        (command): command is Extract<Command, CommandArray> =>
          Array.isArray(command) && command[0] === 'tag',
      ) || [];
    return {
      tagId,
      impressionId: options!.extras.iid || '',
    };
  } catch (e: unknown) {
    const noCommandPayload = { tagId: '0', impressionId: '' };

    log({
      ...noCommandPayload,
      reason: 'lib-namespace-err',
      detail: (e as Error).stack || e,
    });

    return noCommandPayload;
  }
};

const commandIngestion$ = () =>
  make<Command>((observer) => {
    let cancelled = false;
    // Legacy - mutation to imitate `array.push` to consume commands
    // except now we'll just emit new values to the stream 🙌
    // This changes window.mobkoi from an [] to {} and signifies that we've
    // loaded on the page - tag response looks for this flag to check if it needs
    // to fetch the lib or just add the command to the queue
    window.MOBKOI = {
      push: (command: Command) => {
        if (!cancelled) {
          observer.next(command);
        }
      },
      loaded: 1,
    };
    return () => {
      cancelled = true;
    };
  });

/**
 * If the safeframe isn't registered then theres a chance we will inaccurately
 * report the size of the unit the creative can fill. We need to pause execution
 * until the safeframe is registered so we can get the correct dimensions
 */
const waitForSafeFrameRegistered$ = (
  tagId: string | undefined,
  impressionId: string,
) =>
  fromPromise(
    new Promise<void>((resolve) => {
      const environment = getEnvironment();

      // If not in safeframe then we can immediately resolve this promise
      if (!environment.safeFrame) {
        resolve();
        return;
      }

      log({
        reason: 'safeframe-wait',
        tagId: tagId || '',
        impressionId: impressionId || '',
      });

      const startTime = Date.now();
      environment.isSafeFrameRegistered(() => {
        log({
          reason: 'safeframe-registered',
          tagId: tagId || '',
          impressionId: impressionId || '',
          tagd: Date.now() - startTime,
        });
        resolve();
      });
    }),
  );

export const initialise$ = (startSignal: string) =>
  pipe(
    fromValue(startSignal),
    map(() => getFirstCommand()),
    tap(({ tagId, impressionId }) =>
      log({
        reason: 'lib-init',
        impressionId: impressionId || '',
        tagId: tagId || '',
      }),
    ),
    // Wait for safeFrame to be registered before processing commands as we will
    // attempt to grab page dimensions that haven't been initialised yet
    mergeMap(({ tagId, impressionId }) =>
      waitForSafeFrameRegistered$(tagId, impressionId),
    ),
    // Grab commands that exist on our window object for processing
    map(() => (Array.isArray(window.MOBKOI) ? window.MOBKOI : [])),
    // Handle existing tags/commands
    // Then setup a new observable to handle any new commands after first start
    mergeMap((existingCommands) =>
      concat([fromArray(existingCommands), commandIngestion$()]),
    ),
    // Normalise ingested commands to a sane shape to work with
    map(sanitiseCommand),
  );
